/**
 * Auto-reminder top action - resource-agnostic.
 *
 * Off  → an "Enable auto-reminder" button (one click turns it on).
 * On   → a "[bell] Auto-reminder enabled" status button; clicking it opens the
 *        details modal (last sent, next send, template, cadence, cancel).
 *
 * Drop it into any record header's action row next to the workflow action menu;
 * gate the call site with `<FeatureGate flag="auto_reminders">`. Renders nothing
 * until the status resolves, and nothing at all when the tenant has not
 * configured the reminder (no template/trigger) - so it never offers an action
 * that cannot work.
 */
import { useState } from 'react'
import { Bell, BellRing } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { toastApiError } from '@/shared/lib/api-error'

import { AutoReminderDetailsModal } from './auto-reminder-details-modal'
import { useReminderStatus, useSetReminder } from './use-auto-reminder'

const BASE_BUTTON_CLASS =
  'flex cursor-pointer items-center gap-1.5 rounded-[6px] border px-3 py-1.5 text-[11px] transition-all duration-150'

export function AutoReminderAction({
  resourceType,
  resourceUuid,
}: {
  resourceType: string
  resourceUuid: string
}) {
  const { t } = useTranslation()
  const { data: status, isPending } = useReminderStatus(
    resourceType,
    resourceUuid,
  )
  const setReminder = useSetReminder(resourceType)
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Nothing to show until we know, and nothing when the tenant has no reminder
  // configured (no template/trigger) - enabling it would be a no-op sender.
  if (isPending || !status || !status.configured) return null

  const enable = () => {
    setReminder.mutate(
      { uuid: resourceUuid, enabled: true },
      {
        onSuccess: () =>
          toast.success(t('reminders.enabledToast', 'Auto-reminder enabled.')),
        onError: (err) => toastApiError(err, 'Failed to enable auto-reminder'),
      },
    )
  }

  if (!status.enabled) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={enable}
        disabled={setReminder.isPending}
        className={cn(
          BASE_BUTTON_CLASS,
          'border-[var(--rule-strong)] bg-[var(--status-info-bg)] text-[var(--ink)] hover:border-[var(--ink-3)]',
        )}
        data-testid="auto-reminder-enable"
      >
        <Bell className="h-3.5 w-3.5" />
        {setReminder.isPending
          ? t('reminders.enabling', 'Enabling...')
          : t('reminders.enable', 'Enable auto-reminder')}
      </Button>
    )
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setDetailsOpen(true)}
        className={cn(
          BASE_BUTTON_CLASS,
          'border-[var(--status-ok)]/40 bg-[var(--status-ok-bg)] text-[var(--status-ok)] hover:border-[var(--status-ok)]',
        )}
        data-testid="auto-reminder-status"
      >
        <BellRing className="h-3.5 w-3.5" />
        {t('reminders.enabled', 'Auto-reminder enabled')}
      </Button>
      <AutoReminderDetailsModal
        resourceType={resourceType}
        uuid={resourceUuid}
        status={status}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />
    </>
  )
}
